import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUserPlus, FaTrash, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import superadminService from '../../../services/superadminService';

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchBar = styled.div`
  position: relative;
  width: 250px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.1);
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 0.875rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    background: #ff5252;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
  border-bottom: 1px solid #f0f0f0;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.875rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.25rem;
  transition: all 0.2s;
  font-size: 0.75rem;

  &:hover {
    background: #f8f9fa;
    border-color: #ccc;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  
  p {
    margin: 0.5rem 0;
  }
`;

interface UserTenant {
  user_id: number;
  tenant_id: string;
  role: string;
  is_active: boolean;
  is_default: boolean;
  user: {
    id: number;
    email: string;
    full_name: string;
    is_active: boolean;
  };
}

interface TenantUsersProps {
  tenantId: string;
  tenantName?: string;
}

export default function TenantUsers({ tenantId, tenantName }: TenantUsersProps) {
  const [users, setUsers] = useState<UserTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await superadminService.getTenantUsers(tenantId);
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário do tenant?')) {
      return;
    }

    try {
      await superadminService.removeUserFromTenant(userId, tenantId);
      toast.success('Usuário removido com sucesso');
      loadUsers();
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  const handleUpdateRole = async (userId: number, newRole: string) => {
    try {
      await superadminService.updateUserInTenant(userId, tenantId, { role: newRole });
      toast.success('Role atualizado com sucesso');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Erro ao atualizar role');
    }
  };

  const handleToggleActive = async (userId: number, currentStatus: boolean) => {
    try {
      await superadminService.updateUserInTenant(userId, tenantId, { is_active: !currentStatus });
      toast.success('Status atualizado com sucesso');
      loadUsers();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const filteredUsers = users.filter(ut => 
    ut.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ut.user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Container>Carregando usuários...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Usuários do Tenant</Title>
        <Actions>
          <SearchBar>
            <SearchInput
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchIcon />
          </SearchBar>
          <Button onClick={() => setShowAddModal(true)}>
            <FaUserPlus /> Adicionar Usuário
          </Button>
        </Actions>
      </Header>

      {filteredUsers.length === 0 ? (
        <EmptyState>
          <p>Nenhum usuário encontrado</p>
          <p>Clique em "Adicionar Usuário" para adicionar o primeiro usuário ao tenant.</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Email</Th>
              <Th>Nome</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Padrão</Th>
              <Th>Ações</Th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(ut => (
              <tr key={ut.user_id}>
                <Td>{ut.user.email}</Td>
                <Td>{ut.user.full_name || '-'}</Td>
                <Td>
                  <select
                    value={ut.role}
                    onChange={(e) => handleUpdateRole(ut.user_id, e.target.value)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                </Td>
                <Td>
                  <StatusBadge active={ut.is_active}>
                    {ut.is_active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                </Td>
                <Td>
                  <input
                    type="checkbox"
                    checked={ut.is_default}
                    onChange={async () => {
                      try {
                        await superadminService.updateUserInTenant(ut.user_id, tenantId, { 
                          is_default: !ut.is_default 
                        });
                        toast.success('Tenant padrão atualizado');
                        loadUsers();
                      } catch (error) {
                        toast.error('Erro ao atualizar tenant padrão');
                      }
                    }}
                  />
                </Td>
                <Td>
                  <ActionButton onClick={() => handleToggleActive(ut.user_id, ut.is_active)}>
                    {ut.is_active ? 'Desativar' : 'Ativar'}
                  </ActionButton>
                  <ActionButton onClick={() => handleRemoveUser(ut.user_id)}>
                    <FaTrash />
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {showAddModal && (
        <AddUserModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={() => {
            setShowAddModal(false);
            loadUsers();
          }}
        />
      )}
    </Container>
  );
}

// Modal component for adding users
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

interface AddUserModalProps {
  tenantId: string;
  tenantName?: string;
  onClose: () => void;
}

function AddUserModal({ tenantId, tenantName, onClose }: AddUserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'agent'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await superadminService.addUserToTenant(tenantId, formData);
      toast.success('Usuário adicionado com sucesso');
      onClose();
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Adicionar Usuário ao Tenant</ModalTitle>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Nome Completo</Label>
            <Input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Senha</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Deixe em branco se o usuário já existir"
            />
          </FormGroup>

          <FormGroup>
            <Label>Role</Label>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="viewer">Viewer</option>
              <option value="agent">Agent</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose} style={{ background: '#6c757d' }}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adicionando...' : 'Adicionar'}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
}